import DubMediaPlayer from "@/components/elements/DubMediaPlayer";
import { useAppDispatch, useTypedSelector } from "@/config/configureAppStore";
import { ISampleFile } from "@/features/clientProjectDetails";
import useMediaBreakpoints from "@/features/clientProjectDetails/hooks/useMediaBreakpoints";
import { MEDIA_TYPE_EXTN } from "@/features/clientSearch/constant";
import useFetchSampleFiles from "@/features/createProject/hooks/useFetchSampleFiles";
import { MediaPlayerProvider } from "@/features/mediaPlayer";
import {
  setSelectedSampleFile,
  setSelectedSampleFilesBulk
} from "@/reducers/slices/clientProjectPopupSlice/clientProjectPopupSlice";
import { BASE_URL } from "@/utils/links";
import { getSeekerTime } from "@/utils/time";
import {
  CloseRounded,
  PlayCircleFilledRounded,
  VideocamRounded
} from "@mui/icons-material";
import {
  Box,
  Card,
  CardContent,
  Checkbox,
  Dialog,
  IconButton,
  Skeleton,
  Stack,
  Typography
} from "@mui/material";
import React, { useEffect, useState } from "react";

const SelectSampleFiles = (): React.ReactNode => {
  const dispatch = useAppDispatch();
  const [isPlayerOpened, setIsPlayerOpened] = useState(false);
  const [currentlyPlaying, setCurrentlyPlaying] = useState<ISampleFile | null>(
    null
  );

  const selectedVideos = useTypedSelector(
    (state) => state.clientProjectPopup.selectedSampleFiles
  );
  const uploadedSampleFileIds = useTypedSelector(
    (state) => state.clientProjectPopup.uploadedSampleFileIds
  );

  const setSelectedVideo = (file: ISampleFile) => {
    dispatch(setSelectedSampleFile(file));
  };

  const { smallScreens } = useMediaBreakpoints();

  const { isLoading, data, isError } = useFetchSampleFiles();

  const getPublicSampleFileThumbnailUrl = (thumbnailUrl: string) => {
    if (thumbnailUrl) {
      const url = new URL(thumbnailUrl);
      const pathName = url.pathname;
      if (pathName) {
        return BASE_URL + pathName;
      }
    }
    return thumbnailUrl;
  };

  const togglePlayer = (state: boolean) => {
    setIsPlayerOpened(state);
  };

  const isFileSelected = (fileId: string) => {
    return selectedVideos?.some((f) => f.fileId === fileId) || false;
  };

  useEffect(() => {
    if (uploadedSampleFileIds && uploadedSampleFileIds?.length > 0 && data) {
      const selectedFiles = data?.filter((file: ISampleFile) =>
        uploadedSampleFileIds.includes(file.fileId)
      );
      dispatch(setSelectedSampleFilesBulk(selectedFiles));
    }
  }, [uploadedSampleFileIds, data, dispatch]);

  if (isLoading) {
    return (
      <Stack mt={5} gap={1}>
        <Skeleton variant="text" width={"20ch"} height={"32px"}></Skeleton>
        <Skeleton variant="text" width={"50ch"} height={"20px"}></Skeleton>
        <Stack
          direction={"row"}
          gap={2}
          overflow={"auto"}
          flexWrap={"nowrap"}
          py={1}
        >
          {Array(4)
            .fill(0)
            .map((_, index) => (
              <Skeleton
                key={index}
                variant="rectangular"
                width={220}
                height={116}
                sx={{
                  p: 1.5,
                  borderRadius: 3
                }}
              />
            ))}
        </Stack>
      </Stack>
    );
  }

  if (!data || data.length === 0 || isError) {
    return null;
  }

  return (
    <>
      <Stack mt={5} gap={1}>
        <Typography variant="h6" color={"text.primary"}>
          Don’t have a file?
        </Typography>
        <Typography variant="body2" color={"text.primary"}>
          Try dubbing some sample files. You won’t be charged for dubbing sample
          files
        </Typography>
        <Stack
          direction={"row"}
          gap={2}
          overflow={"auto"}
          flexWrap={"nowrap"}
          py={1}
        >
          {data.map((file: ISampleFile, index: number) => {
            return (
              <Card
                key={index}
                sx={{
                  flexShrink: 0,
                  p: 0.25,
                  bgcolor: "transparent",
                  backdropFilter: "none",
                  boxShadow: "none",
                  cursor: "pointer",
                  borderRadius: 3,
                  border: `2px solid transparent`,
                  position: "relative",
                  ...(isFileSelected(file.fileId)
                    ? {
                        borderColor: (theme) => theme.palette.action.active
                      }
                    : {
                        [`&:hover:not(:has(.sample-file-play-button:hover))`]: {
                          borderColor: (theme) =>
                            theme.palette.secondary.outlinedBorder,
                          backgroundColor: (theme) =>
                            theme.palette.secondary.hover
                        }
                      })
                }}
                component={Box}
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedVideo(file);
                }}
              >
                <CardContent
                  sx={{
                    background: `linear-gradient(180deg, rgba(0, 0, 0, 0.00) 56.73%, rgba(51, 49, 61, 0.28) 72.12%, #33313D 100%), linear-gradient(0deg, rgba(76, 74, 92, 0.50) 0%, rgba(76, 74, 92, 0.50) 100%), url(${getPublicSampleFileThumbnailUrl(
                      file.thumbnailUrl as string
                    )}) lightgray 50% / cover no-repeat`,
                    height: 116,
                    borderRadius: 2,
                    p: 3,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    alignItems: "center",
                    gap: 0,
                    width: 220
                  }}
                >
                  <IconButton
                    onClick={(e) => {
                      e.stopPropagation();
                      setCurrentlyPlaying(file);
                      togglePlayer(true);
                    }}
                    className="sample-file-play-button"
                  >
                    <PlayCircleFilledRounded
                      sx={{
                        height: 32,
                        width: 32,
                        color: "common.white"
                      }}
                    />
                  </IconButton>
                  <Typography
                    variant="body2"
                    color={"common.white"}
                    textAlign={"center"}
                    maxWidth={"20ch"}
                    overflow="hidden"
                    textOverflow="ellipsis"
                    whiteSpace="nowrap"
                  >
                    {file.fileName?.split(".")[0] || file.fileName}
                  </Typography>
                </CardContent>
                <Stack
                  sx={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    zIndex: 1,
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "space-between",
                    alignItems: "center",
                    width: "100%"
                  }}
                >
                  <Checkbox
                    checked={isFileSelected(file.fileId)}
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedVideo(file);
                    }}
                    inputProps={{ "aria-label": "controlled" }}
                    color="secondary"
                    sx={{
                      color: "common.white",
                      "&.Mui-checked": {
                        color: "common.white"
                      }
                    }}
                  />
                  <Typography
                    variant="caption"
                    color={"common.white"}
                    px={1.5}
                    py={1}
                  >
                    {getSeekerTime({
                      timeValue: file.duration,
                      isCyclic: Boolean(file.duration && file.duration >= 3600)
                    })}
                  </Typography>
                </Stack>
              </Card>
            );
          })}
        </Stack>
      </Stack>
      {/* Media Player */}
      <Dialog
        open={isPlayerOpened}
        onClose={(e: any) => {
          e.stopPropagation();
          togglePlayer(false);
        }}
        fullWidth
        maxWidth={"md"}
        sx={{
          borderRadius: 4
        }}
        PaperProps={{
          sx: {
            borderRadius: 4
          }
        }}
      >
        <Box
          sx={(theme) => ({
            p: 2,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: 2,
            flexDirection: "column",
            borderRadius: 4,
            width: "100%",
            [theme.breakpoints.up("md")]: {
              minWidth: 640
            },
            [theme.breakpoints.down("md")]: {
              minWidth: 500
            }
          })}
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          <Stack
            direction={"row"}
            justifyContent={"flex-start"}
            alignItems={"center"}
            gap={2}
            width={"100%"}
          >
            <VideocamRounded />
            <Typography
              id="modal-modal-title"
              variant="h6"
              noWrap
              textOverflow={"ellipsis"}
            >
              {currentlyPlaying?.fileName}
            </Typography>
            <IconButton
              sx={{ ml: "auto" }}
              onClick={(e) => {
                e.stopPropagation();
                setIsPlayerOpened(false);
              }}
            >
              <CloseRounded />
            </IconButton>
          </Stack>
          <Box
            borderRadius={2}
            sx={{
              height: "100%",
              width: "100%",
              flexGrow: 1,
              display: "flex"
            }}
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            {currentlyPlaying ? (
              <MediaPlayerProvider
                id={`media_player_${currentlyPlaying?.fileId}`}
              >
                <DubMediaPlayer
                  mediaPlayerId={`media_player_${currentlyPlaying?.fileId}`}
                  mediaSrc={currentlyPlaying.url}
                  type={MEDIA_TYPE_EXTN.VIDEO}
                  boxStyleProps={{
                    display: "flex",
                    width: "100%",
                    aspectRatio: "16 / 9",
                    backgroundColor: "black",
                    maxHeight: smallScreens ? "60vh" : "unset",
                    alignItems: "end",
                    justifyContent: "space-between"
                  }}
                  playerWrapperStyles={{
                    margin: smallScreens ? "0 auto" : "unset"
                  }}
                />
              </MediaPlayerProvider>
            ) : null}
          </Box>
        </Box>
      </Dialog>
      {/* [END] Media Player */}
    </>
  );
};

export default SelectSampleFiles;
